/**
 * Global plan-limit modal store (2026-08-31, Jaime feature):
 * ANY API 403 with a plan-limit rejection must open an upgrade-suggest modal
 * app-wide (not just a per-page error toast).
 *
 * Detection marker (backend, billing/plan-limits.checkCapacity and plan-gate):
 *   403 body { success:false, code:'PLAN_LIMIT', module, current_limit,
 *   current_usage, error, message }. `code === 'PLAN_LIMIT'` is the stable
 *   marker; message regex is only a fallback for older/no-code payloads.
 */
import type { AxiosError } from 'axios';

export interface PlanLimitInfo {
  message: string;
  module?: string | null;
  limit?: number | null;
  usage?: number | null;
}

const PLAN_LIMIT_MESSAGE_RE = /alcanzado el l[íi]mite de .*de tu plan/i;

/** Detect a backend plan-limit rejection (stable code first, message fallback). */
export function isPlanLimitError(err: unknown): boolean {
  const ax = err as AxiosError<any> | undefined;
  if (!ax?.response || ax.response.status !== 403) return false;
  const data = ax.response.data as any;
  if (!data) return false;
  if (data.code === 'PLAN_LIMIT') return true;
  const msg = String(data.message || data.error || '');
  return PLAN_LIMIT_MESSAGE_RE.test(msg);
}

/**
 * Bug #111 (2026-09-24): friendly Spanish copy per module for the modal body.
 * The backend plan-limit payload carries a raw English message
 * (e.g. "Free plan pets limit reached.") — never user-facing here.
 */
const MODULE_LIMIT_TEXT: Record<string, string> = {
  pets: 'Alcanzaste el límite de mascotas de tu plan. Mejora a un plan superior para agregar más.',
  vehicles: 'Alcanzaste el límite de vehículos de tu plan. Mejora a un plan superior para agregar más.',
  tasks: 'Alcanzaste el límite de tareas de tu plan. Mejora a un plan superior para crear más.',
  shopping: 'Alcanzaste el límite de la lista de compras de tu plan. Mejora a un plan superior para agregar más.',
  notes: 'Alcanzaste el límite de notas de tu plan. Mejora a un plan superior para agregar más.',
  budget: 'Alcanzaste el límite del presupuesto de tu plan. Mejora a un plan superior para agregar más.',
};
const GENERIC_LIMIT_TEXT = 'Alcanzaste el límite de tu plan. Mejora a un plan superior para continuar.';

/** Extract display info from a plan-limit rejection (friendly Spanish message). */
export function planLimitInfoFromError(err: unknown): PlanLimitInfo {
  const data = (err as AxiosError<any>)?.response?.data as any;
  const moduleName = data?.module && typeof data.module === 'string' ? data.module : null;
  const msg = (moduleName && MODULE_LIMIT_TEXT[moduleName]) || GENERIC_LIMIT_TEXT;
  return {
    message: msg,
    module: moduleName,
    limit: typeof data?.current_limit === 'number' ? data.current_limit : null,
    usage: typeof data?.current_usage === 'number' ? data.current_usage : null,
  };
}

type Listener = (info: PlanLimitInfo | null) => void;

let current: PlanLimitInfo | null = null;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(current);
}

/** Open the global PlanLimitModal. Dedupes concurrent/parallel rejections. */
export function showPlanLimitModal(info: PlanLimitInfo): void {
  if (current && current.message === info.message && current.module === info.module) {
    return; // dedupe: same limit already showing
  }
  current = info;
  emit();
}

export function closePlanLimitModal(): void {
  current = null;
  emit();
}

export function subscribePlanLimitModal(listener: Listener): () => void {
  listeners.add(listener);
  listener(current); // sync initial state
  return () => listeners.delete(listener);
}

/**
 * Bug #107 (2026-09-24): shared page-level error text that must not duplicate
 * the friendly copy the global PlanLimitModal already shows. Returns null for
 * a plan-limit 403 (caller should skip setError — the modal covers it) and
 * otherwise a backend-provided message or the caller's fallback — never the
 * raw axios "Request failed with status code NNN".
 */
export function pageErrorFromApi(err: any, fallback: string): string | null {
  if (isPlanLimitError(err)) return null;
  const status = Number(err?.response?.status || 0);
  const data = err?.response?.data || {};
  const candidates = [
    data.message,
    typeof data.error === 'object' && data.error !== null ? data.error?.message : data.error,
    data.details,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && !/^internal server error$/i.test(c.trim())) return c.trim();
  }
  if (!status || status >= 500) return 'Error del servidor. Inténtalo de nuevo en unos momentos.';
  return fallback;
}

/** Merge a fetched usage count into the open modal (payloads lacking current_usage). */
export function updatePlanLimitUsage(module: string, usage: number): void {
  if (!current || current.module !== module || typeof current.usage === 'number') return;
  current = { ...current, usage };
  emit();
}

export function getPlanLimitState(): PlanLimitInfo | null {
  return current;
}
